<template>
  <div class="wholesaler-after-sales">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="back-btn" @click="goBack">← 返回 / Back</div>
      <h1>售后管理 / After Sales</h1>
    </div>

    <div v-loading="loading" class="content">
      <!-- 统计卡片 -->
      <div class="stats-row">
        <div class="stat-card warn" @click="filterStatus = 'PENDING_REVIEW'">
          <div class="stat-num">{{ stats.pending }}</div>
          <div class="stat-label">待审核</div>
        </div>
        <div class="stat-card" @click="filterStatus = 'REVIEWED'">
          <div class="stat-num">{{ stats.reviewed }}</div>
          <div class="stat-label">处理中</div>
        </div>
        <div class="stat-card success" @click="filterStatus = 'COMPLETED'">
          <div class="stat-num">{{ stats.completed }}</div>
          <div class="stat-label">已完成</div>
        </div>
      </div>

      <!-- 筛选标签 -->
      <div class="filter-tabs">
        <span
          v-for="tab in filterTabs"
          :key="tab.value"
          class="filter-tab"
          :class="{ active: filterStatus === tab.value }"
          @click="filterStatus = tab.value; loadList()"
        >
          {{ tab.label }}
          <span v-if="tab.count" class="count">({{ tab.count }})</span>
        </span>
      </div>

      <!-- 售后列表 -->
      <div v-if="list.length > 0" class="list">
        <div
          v-for="item in list"
          :key="item.id"
          class="as-card"
          :class="{ highlight: item.status === 'PENDING_REVIEW' }"
          @click="goDetail(item.id)"
        >
          <div class="as-card-header">
            <span class="as-no">{{ item.afterSalesNo }}</span>
            <span class="as-status" :class="statusClass(item.status)">{{ statusText(item.status) }}</span>
          </div>
          <div class="as-card-body">
            <div class="as-left">
              <div class="as-reason">{{ reasonIcon(item.reasonCode) }} {{ reasonText(item.reasonCode) }}</div>
              <div class="as-customer">客户: {{ item.customerName || '-' }}</div>
              <div class="as-order">订单: {{ item.orderNo }}</div>
              <div class="as-time">{{ item.requestedAt }} · {{ schemeText(item.resolutionType) }}</div>
            </div>
            <div class="as-right">
              <div class="as-amount">S$ {{ centsToAmount(item.refundAmount) }}</div>
              <div class="as-sub">{{ resolutionStatusText(item.resolutionStatus) }}</div>
              <el-button
                v-if="item.status === 'PENDING_REVIEW'"
                size="mini"
                type="primary"
                class="mt4"
                @click.stop="goReview(item.id)"
              >
                去审核
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon">📋</div>
        <div class="empty-text">暂无售后记录</div>
      </div>

      <!-- 分页 -->
      <div v-if="totalPages > 1" class="pagination">
        <el-button size="mini" :disabled="page === 0" @click="page--; loadList()">上一页</el-button>
        <span class="page-info">{{ page + 1 }} / {{ totalPages }}</span>
        <el-button size="mini" :disabled="page >= totalPages - 1" @click="page++; loadList()">下一页</el-button>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>首页</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>商品</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>批发价</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>订单</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>我的</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getAfterSalesList } from '@/services/afterSales';

export default {
  name: 'WholesalerAfterSales',
  data() {
    return {
      loading: false,
      list: [],
      page: 0,
      pageSize: 20,
      totalPages: 1,
      total: 0,
      filterStatus: '',
      stats: { pending: 0, reviewed: 0, completed: 0 }
    };
  },
  computed: {
    filterTabs() {
      return [
        { label: '全部', value: '' },
        { label: '待审核', value: 'PENDING_REVIEW', count: this.stats.pending },
        { label: '处理中', value: 'REVIEWED', count: this.stats.reviewed },
        { label: '已完成', value: 'COMPLETED', count: this.stats.completed }
      ];
    }
  },
  created() {
    this.loadList();
  },
  methods: {
    async loadList() {
      this.loading = true;
      try {
        const params = {
          status: this.filterStatus || undefined,
          page: this.page,
          size: this.pageSize
        };
        // 支持从订单页面传入 orderId
        const orderId = this.$route.query.orderId;
        if (orderId) params.orderId = orderId;
        const res = await getAfterSalesList(params);
        // Spring Data Page response
        this.list = res.content || [];
        this.totalPages = res.totalPages || 1;
        this.total = res.totalElements || 0;

        // 计算统计
        this.stats.pending = 0;
        this.stats.reviewed = 0;
        this.stats.completed = 0;
        this.list.forEach(item => {
          if (item.status === 'PENDING_REVIEW') this.stats.pending++;
          else if (item.status === 'REVIEWED') this.stats.reviewed++;
          else if (item.status === 'COMPLETED') this.stats.completed++;
        });
      } catch (e) {
        this.$message.error('加载售后列表失败: ' + (e.message || e));
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push('/wholesaler/home');
    },
    goDetail(id) {
      this.$router.push(`/wholesaler/after-sales/${id}`);
    },
    goReview(id) {
      this.$router.push(`/wholesaler/after-sales/${id}/review`);
    },
    statusClass(status) {
      const map = {
        PENDING_REVIEW: 'status-pending',
        REVIEWED: 'status-reviewed',
        COMPLETED: 'status-completed',
        REJECTED: 'status-rejected',
        CANCELLED: 'status-cancelled'
      };
      return map[status] || '';
    },
    statusText(status) {
      const map = {
        PENDING_REVIEW: '⏳ 待审核',
        REVIEWED: '🔄 处理中',
        COMPLETED: '✅ 已完成',
        REJECTED: '❌ 已拒绝',
        CANCELLED: '🚫 已取消'
      };
      return map[status] || status;
    },
    reasonIcon(code) {
      const map = {
        DAMAGED: '📦',
        WRONG_ITEM: '❌',
        NOT_RECEIVED: '❓',
        WRONG_SPEC: '📏',
        SHORT_DELIVERY: '📉',
        DONT_WANT: '🙅',
        OTHER: '📝'
      };
      return map[code] || '📋';
    },
    reasonText(code) {
      const map = {
        DAMAGED: '商品破损',
        WRONG_ITEM: '发错货了',
        NOT_RECEIVED: '没收到货',
        WRONG_SPEC: '规格不对',
        SHORT_DELIVERY: '少发了',
        DONT_WANT: '不想要了',
        OTHER: '其他原因'
      };
      return map[code] || code;
    },
    schemeText(type) {
      const map = {
        RETURN_REFUND: '方案: 退货退款',
        EXCHANGE: '方案: 换货',
        RESEND: '方案: 补发',
        PARTIAL_REFUND: '方案: 部分退款',
        RELIEF: '方案: 减免',
        NO_ACTION: '方案: 不处理'
      };
      return map[type] || '';
    },
    resolutionStatusText(status) {
      if (!status) return '';
      const map = {
        PICKING_UP: '取货中',
        RETURN_RECEIVED: '已收到退货',
        INSPECTING: '质检中',
        INSPECTION_FAILED: '质检不通过',
        REFUND_PROCESSING: '退款处理中',
        EXCHANGE_SHIPPING: '换货发货中',
        EXCHANGE_DELIVERED: '换货已送达',
        RESEND_SHIPPING: '补发发货中',
        RESEND_DELIVERED: '补发已送达',
        COMPLETED: '已完成'
      };
      return map[status] || status;
    },
    centsToAmount(cents) {
      if (!cents) return '0.00';
      return (cents / 100).toFixed(2);
    },
    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1d3557, #457b9d);
  color: #fff;
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
}
.back-btn { font-size: 12px; opacity: 0.8; cursor: pointer; margin-bottom: 4px; }
.back-btn:hover { opacity: 1; }
.page-header h1 { font-size: 17px; }

.content { max-width: 600px; margin: 0 auto; padding-bottom: 20px; }

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px;
}
.stat-card {
  text-align: center;
  padding: 14px 8px;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.stat-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.stat-num { font-size: 22px; font-weight: 700; color: #1d3557; }
.stat-card.warn .stat-num { color: #fa8c16; }
.stat-card.success .stat-num { color: #52c41a; }
.stat-label { font-size: 11px; color: #999; margin-top: 4px; }

.filter-tabs {
  display: flex;
  background: #fff;
  padding: 0 12px;
  gap: 0;
  margin-bottom: 8px;
}
.filter-tab {
  padding: 10px 12px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
}
.filter-tab.active { color: #1d3557; font-weight: 600; border-bottom-color: #1d3557; }
.filter-tab .count { font-size: 11px; color: #999; margin-left: 2px; }

.as-card {
  background: #fff;
  margin: 0 8px 8px;
  border-radius: 8px;
  padding: 14px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.as-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.as-card.highlight { border-left: 3px solid #fa8c16; }

.as-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.as-no { font-size: 12px; color: #666; font-family: monospace; }
.as-status { font-size: 11px; padding: 2px 8px; border-radius: 10px; }
.status-pending { background: #fff7e6; color: #fa8c16; }
.status-reviewed { background: #e6f7ff; color: #1890ff; }
.status-completed { background: #f6ffed; color: #52c41a; }
.status-rejected { background: #fff2f0; color: #ff4d4f; }
.status-cancelled { background: #fafafa; color: #999; }

.as-card-body { display: flex; justify-content: space-between; }
.as-left { flex: 1; }
.as-reason { font-size: 13px; margin-bottom: 4px; }
.as-customer { font-size: 12px; color: #666; }
.as-order { font-size: 11px; color: #457b9d; margin-top: 2px; }
.as-time { font-size: 11px; color: #bbb; margin-top: 2px; }
.as-right { text-align: right; margin-left: 8px; }
.as-amount { font-size: 15px; color: #ff4d4f; font-weight: 600; }
.as-sub { font-size: 11px; color: #1890ff; margin-top: 2px; }
.mt4 { margin-top: 6px; }

.empty-state { text-align: center; padding: 60px 20px; color: #999; }
.empty-icon { font-size: 48px; margin-bottom: 12px; }
.empty-text { font-size: 14px; }

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 16px;
}
.page-info { font-size: 13px; color: #666; }

/* 搴曢儴瀵艰埅 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}
</style>
